import { ClientBusinessPartialModel } from 'app/client/clientBusiness/models/ClientBusiness';
import { Form, Formik, FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { Calendar, FileText, Users } from 'lucide-react';
import { KeyboardEvent, ReactNode, useState } from 'react';
import AnimatedStepPanel from 'shared/components/AnimatedStepPanel';
import { ApplicantRoleModel } from '../../models/ApplicantRole';
import { CreateEventPayload } from '../../services/useEventoService';
import EventoDatesStep, { datesFields, datesSchema } from './EventoDatesStep';
import EventoGeneralDataStep, {
  generalDataFields,
  generalDataSchema
} from './EventoGeneralDataStep';
import EventoReferentsStep, {
  referentsFields,
  referentsSchema
} from './EventoReferentsStep';
import {
  EventoWizardFormValues,
  eventoWizardInitialValues
} from './eventoWizardTypes';

type StepKey = 'general' | 'fechas' | 'referentes';

const STEPS: { key: StepKey; label: string; icon: ReactNode }[] = [
  { key: 'general', label: 'Datos generales', icon: <FileText size={16} /> },
  { key: 'fechas', label: 'Fechas', icon: <Calendar size={16} /> },
  { key: 'referentes', label: 'Referentes', icon: <Users size={16} /> }
];

const STEP_FIELDS: Record<StepKey, readonly (keyof EventoWizardFormValues)[]> = {
  general: generalDataFields,
  fechas: datesFields,
  referentes: referentsFields
};

const fullValidationSchema = generalDataSchema
  .concat(datesSchema)
  .concat(referentsSchema);

const buildTouchedFor = (
  fields: readonly (keyof EventoWizardFormValues)[],
  values: EventoWizardFormValues
) => {
  const touched: Record<string, unknown> = {};
  fields.forEach((field) => {
    if (field === 'referents') {
      touched.referents = values.referents.map(() => ({
        name: true,
        roleId: true,
        email: true,
        phone: true
      }));
    } else {
      touched[field] = true;
    }
  });
  return touched;
};

const hasErrorsForFields = (
  errors: FormikErrors<EventoWizardFormValues>,
  fields: readonly (keyof EventoWizardFormValues)[]
) => fields.some((field) => errors[field] !== undefined);

const handleFormKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
  const target = event.target as HTMLElement;
  if (event.key === 'Enter' && target.tagName === 'INPUT') {
    event.preventDefault();
  }
};

interface Props {
  clients: ClientBusinessPartialModel[];
  roles: ApplicantRoleModel[];
  isSubmitting: boolean;
  onSubmit: (payload: CreateEventPayload) => void;
}

const EventoCreateWizard = ({ clients, roles, isSubmitting, onSubmit }: Props) => {
  const [currentStep, setCurrentStep] = useState<StepKey>('general');
  const [direction, setDirection] = useState<1 | -1>(1);

  const goTo = (step: StepKey, dir: 1 | -1) => {
    setDirection(dir);
    setCurrentStep(step);
  };

  const handleFinalSubmit = (values: EventoWizardFormValues) => {
    onSubmit({
      name: values.name,
      description: values.description || undefined,
      business_client_id: values.businessClientId as number,
      is_active: values.isActive,
      from_date: values.fromDate,
      to_date: values.toDate,
      referents: values.referents.map((referent) => ({
        name: referent.name,
        role_id: referent.roleId as number,
        email: referent.email || undefined,
        phone: referent.phone || undefined
      }))
    });
  };

  return (
    <Formik<EventoWizardFormValues>
      initialValues={eventoWizardInitialValues}
      validationSchema={fullValidationSchema}
      onSubmit={handleFinalSubmit}
    >
      <Form onKeyDown={handleFormKeyDown}>
        <WizardBody
          clients={clients}
          roles={roles}
          isSubmitting={isSubmitting}
          currentStep={currentStep}
          direction={direction}
          goTo={goTo}
        />
      </Form>
    </Formik>
  );
};

interface WizardBodyProps {
  clients: ClientBusinessPartialModel[];
  roles: ApplicantRoleModel[];
  isSubmitting: boolean;
  currentStep: StepKey;
  direction: 1 | -1;
  goTo: (step: StepKey, dir: 1 | -1) => void;
}

const WizardBody = ({
  clients,
  roles,
  isSubmitting,
  currentStep,
  direction,
  goTo
}: WizardBodyProps) => {
  const formik = useFormikContext<EventoWizardFormValues>();

  const handleNext = async (
    fields: readonly (keyof EventoWizardFormValues)[],
    nextStep: StepKey
  ) => {
    const errors = await formik.validateForm();
    formik.setTouched(
      {
        ...formik.touched,
        ...buildTouchedFor(fields, formik.values)
      } as FormikTouched<EventoWizardFormValues>,
      false
    );
    if (!hasErrorsForFields(errors, fields)) {
      goTo(nextStep, 1);
    }
  };

  const currentIndex = STEPS.findIndex((step) => step.key === currentStep);

  return (
    <div className="flex flex-col gap-10 md:flex-row">
      <div className="flex shrink-0 flex-row gap-4 md:w-56 md:flex-col md:gap-0">
        {STEPS.map((step, index) => {
          const isActive = step.key === currentStep;
          const isDone = index < currentIndex;
          return (
            <div key={step.key} className="flex flex-col">
              <div className="flex items-center gap-3">
                <span
                  className={
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-300 ' +
                    (isActive || isDone
                      ? 'border-secondary-500 bg-secondary-500 text-white'
                      : 'border-line text-inkMuted-dark dark:border-white/10')
                  }
                >
                  {step.icon}
                </span>
                <span
                  className={
                    'text-sm font-medium transition-colors duration-300 ' +
                    (isActive || isDone
                      ? 'text-ink dark:text-ink-dark'
                      : 'text-inkMuted-dark')
                  }
                >
                  {step.label}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <span
                  className={
                    'my-1 ml-[17px] hidden h-8 w-0.5 transition-colors duration-300 md:block ' +
                    (isDone
                      ? 'bg-secondary-500'
                      : 'bg-line dark:bg-white/10')
                  }
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex-1">
        <AnimatedStepPanel stepKey={currentStep} direction={direction}>
          {currentStep === 'general' && (
            <EventoGeneralDataStep
              clients={clients}
              onNext={() => handleNext(STEP_FIELDS.general, 'fechas')}
            />
          )}
          {currentStep === 'fechas' && (
            <EventoDatesStep
              onBack={() => goTo('general', -1)}
              onNext={() => handleNext(STEP_FIELDS.fechas, 'referentes')}
            />
          )}
          {currentStep === 'referentes' && (
            <EventoReferentsStep
              roles={roles}
              isSubmitting={isSubmitting}
              onBack={() => goTo('fechas', -1)}
            />
          )}
        </AnimatedStepPanel>
      </div>
    </div>
  );
};

export default EventoCreateWizard;
